import type { VGroveClientConfig } from "./client";
import type { LayoutSettings } from "./types";
import type { VGroveProviderProps } from "./providers/VGroveProvider";

/**
 * VGrove UI 配置接口 - 扩展 VGroveProviderProps
 */
export interface VGroveUIConfig extends Omit<VGroveProviderProps, 'children'> {
    /** 布局设置 */
    settings?: LayoutSettings;
    /** VGrove Client 配置 */
    clientConfig?: VGroveClientConfig;
    /** 是否自动初始化 VGrove Client */
    autoInitClient?: boolean;
}

/**
 * 快速 debug 配置选项
 */
export interface QuickDebugConfig {
    /** 是否启用调试 */
    enabled?: boolean;
    /** 启用所有调试模块 */
    enableAll?: boolean;
    /** 只启用特定模块 */
    modules?: Array<'auth' | 'i18n' | 'performance' | 'routing' | 'storage'>;
    /** 是否启用开发者工具 */
    devtools?: boolean;
}

/**
 * 创建 VGrove UI 配置
 * @param config VGrove UI 配置选项
 * @returns 完整的配置对象
 */
export function createVGroveUIConfig(config: VGroveUIConfig = {}): VGroveUIConfig {
    const defaultDebugConfig = {
        enabled: false,
        auth: false,
        i18n: false,
        performance: false,
        routing: false,
        storage: false
    };

    // 合并调试配置
    const mergedDebugConfig = {
        ...defaultDebugConfig,
        ...config.settings?.debug,
        ...config.clientConfig?.debug
    };

    return {
        autoInitClient: true,
        ...config,
        settings: {
            ...config.settings,
            debug: mergedDebugConfig
        },
        clientConfig: {
            devtools: process.env.NODE_ENV === 'development',
            ...config.clientConfig,
            debug: mergedDebugConfig
        }
    };
}

/**
 * 快速配置调试功能
 * @param quickConfig 快速调试配置
 * @returns VGrove UI 配置
 */
export function createDebugConfig(quickConfig: QuickDebugConfig): VGroveUIConfig {
    const { enabled = true, enableAll = false, modules = [], devtools } = quickConfig;

    // 如果启用了所有模块
    if (enableAll) {
        return createVGroveUIConfig({
            clientConfig: {
                debug: {
                    enabled: true,
                    auth: true,
                    i18n: true,
                    performance: true,
                    routing: true,
                    storage: true
                },
                devtools: devtools ?? process.env.NODE_ENV === 'development'
            }
        });
    }

    // 根据指定的模块创建配置
    const debugConfig = {
        enabled,
        auth: modules.includes('auth'),
        i18n: modules.includes('i18n'),
        performance: modules.includes('performance'),
        routing: modules.includes('routing'),
        storage: modules.includes('storage')
    };

    return createVGroveUIConfig({
        clientConfig: {
            debug: debugConfig,
            devtools: devtools ?? process.env.NODE_ENV === 'development'
        }
    });
}

/**
 * 开发环境调试配置 - 启用所有调试功能
 */
export const developmentConfig: VGroveUIConfig = createDebugConfig({
    enableAll: true,
    devtools: true
});

/**
 * 生产环境配置 - 关闭所有调试功能
 */
export const productionConfig: VGroveUIConfig = createVGroveUIConfig({
    clientConfig: {
        debug: {
            enabled: false,
            auth: false,
            i18n: false,
            performance: false,
            routing: false,
            storage: false
        },
        devtools: false
    }
});

/**
 * 自动环境配置 - 根据 NODE_ENV 自动选择
 */
export const autoConfig: VGroveUIConfig = process.env.NODE_ENV === 'development'
    ? developmentConfig
    : productionConfig;

/**
 * 创建性能优化的调试配置
 */
export function createOptimizedDebugConfig(): VGroveUIConfig {
    return {
        clientConfig: {
            debug: {
                enabled: process.env.NODE_ENV === 'development',
                auth: false,      // 关闭认证调试，减少输出
                routing: false,   // 关闭路由调试，减少输出
                performance: false, // 关闭性能调试
                i18n: false,      // 关闭国际化调试
                storage: false    // 关闭存储调试
            },
            devtools: process.env.NODE_ENV === 'development'
        },
        autoInitClient: true
    };
}

/**
 * 创建生产环境配置（零调试输出）
 */
export function createProductionConfig(): VGroveUIConfig {
    return {
        clientConfig: {
            debug: {
                enabled: false,
                auth: false,
                routing: false,
                performance: false,
                i18n: false,
                storage: false
            },
            devtools: false
        },
        autoInitClient: true
    };
}

/**
 * 便捷的环境配置工厂函数
 */
export const configs = {
    /** 开发环境配置 */
    development: createVGroveUIConfig({
        clientConfig: {
            debug: {
                enabled: true,
                auth: false,        // 只在需要时启用
                routing: false,     // 只在需要时启用
                performance: false, // 只在需要时启用
                i18n: false,       // 只在需要时启用
                storage: false     // 只在需要时启用
            },
            devtools: true
        },
        autoInitClient: true
    }),
    /** 生产环境配置 */
    production: createProductionConfig(),
    /** 自动环境配置 */
    auto: process.env.NODE_ENV === 'development' ?
        createOptimizedDebugConfig() : // 开发环境使用优化配置
        createProductionConfig(),       // 生产环境使用生产配置
    /** 创建自定义配置 */
    create: createVGroveUIConfig,
    /** 创建调试配置 */
    debug: createDebugConfig,
    /** 优化调试配置 */
    optimized: createOptimizedDebugConfig()
} as const; 